import { useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Server } from '@wajeez-web/core/api-base';
import { ContentEntityListsActions } from '@wajeez-web/stores/content';
import { SuperAdminSystemPermissions } from '@wajeez-web/core/types';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { CheckBoxType } from '@wajeez-web/components/mui';
import {
  DataTableWrapper,
  useContentEntityListsDatatable,
} from '@wajeez-web/components/helpers';
import { Button } from '@wajeez-web/components/ui';
import { PlusIcon } from '@heroicons/react/solid';

import { ContentEntityListModal } from '../components/ContentEntityListModal';
import { ContentEntityListItemsModal } from '../components/ContentEntityListItemsModal';

export const ContentEntityListsPage = () => {
  const [isContentEntityListModalOpen, setIsContentEntityListModalOpen] =
    useState(false);
  const [selectedContentEntityList, setSelectedContentEntityList] =
    useState<Partial<Server.EditContentEntityListInput>>();
  const [
    isContentEntityListItemsModalOpen,
    setIsContentEntityListItemsModalOpen,
  ] = useState(false);

  const dataTable = useContentEntityListsDatatable();

  const { tablePreferances, setTablePreferences, isTableReferencesLoading } =
    HandleTableData(
      'contentEntityLists',
      ContentEntityListsActions.getContentEntityListsDatatableAsync
    );

  return (
    <div className="h-full">
      <PageLayoutsContainer
        title={`Content Entity Lists`}
        withoutRightPadding={false}
        endSideComponent={[
          {
            element: (
              <Button
                title={'Create Content Entity List'}
                Icon={PlusIcon}
                onClick={() => {
                  setSelectedContentEntityList(undefined);
                  setIsContentEntityListModalOpen(true);
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [SuperAdminSystemPermissions.contentEntityLists.add],
          },
        ]}
        breadCrumbsLinks={[{ title: 'Content Entity Lists' }]}
      >
        <DataTableWrapper
          data={dataTable.data as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          isLoading={dataTable.status === 'loading'}
          rowActions={(row) => {
            return [
              {
                title: 'Edit',
                permission: SuperAdminSystemPermissions.contentEntityLists.edit,
                endpoint: '',
                type: 'request',
                action: () => {
                  setSelectedContentEntityList({ id: Number(row[1]) });
                  setIsContentEntityListModalOpen(true);
                },
              },
              {
                title: 'Show Items',
                permission:
                  SuperAdminSystemPermissions.contentEntityLists.getItems,
                endpoint: '',
                type: 'request',
                action: () => {
                  setSelectedContentEntityList({ id: Number(row[1]) });
                  setIsContentEntityListItemsModalOpen(true);
                },
              },
            ];
          }}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
      {isContentEntityListModalOpen && (
        <ContentEntityListModal
          isOpen={isContentEntityListModalOpen}
          onClose={() => {
            setIsContentEntityListModalOpen(false);
            setSelectedContentEntityList(undefined);
          }}
          id={selectedContentEntityList?.id}
          onSubmit={() => {
            setIsContentEntityListModalOpen(false);
            setSelectedContentEntityList(undefined);

            tablePreferances && setTablePreferences({ ...tablePreferances });
          }}
        />
      )}
      {isContentEntityListItemsModalOpen && selectedContentEntityList?.id && (
        <ContentEntityListItemsModal
          isOpen={isContentEntityListItemsModalOpen}
          onClose={() => {
            setIsContentEntityListItemsModalOpen(false);
            setSelectedContentEntityList(undefined);
          }}
          id={selectedContentEntityList.id}
        />
      )}
    </div>
  );
};
